import { Type } from '@angular/core';
import { exists } from '../../functions/exists';
import { PolymorphicDeserializationKey } from '../enum/shared/polymorphic-deserialization-key.enum';
import { DeserializeMenuHelper } from './deserialize-menu-helper';
import { DeserializeSectionHelper } from './deserialize-section-helper';
import { DeserializeLabelHelper } from './deserialize-label-helper';
import { Deserializable } from './deserializable';
import { DeserializeDisplayHelper } from './deserialize-display-helper';
import { DeserializeWebSocketMessageHelper } from './deserialize-web-socket-message-helper';

/**
 * Don't import this class directly, use window?.injector?.Deserialize to access the static methods of this class.
 * Accessing directly will cause circular dependency warnings.
 */
export class InjectedDeserializer {
  /**
   * If ObjectType implements getPolymorphicDeserializationKey, then this method will return a polymorphic object.
   * Example:
   * const Deserialize = window?.injector?.Deserialize;
   * Deserialize?.instanceOf(Label, saleSystemLabelData), will return an object of type SaleSystemLabel and not Label.
   */
  static instanceOf<T extends Deserializable>(ObjectType: Type<T>, data: any, clearMethodCache = true): T {
    if (!data) {
      return null;
    } else {
      const polymorphismKey = Object.create(ObjectType.prototype)?.getPolymorphicDeserializationKey?.();
      const instance: T =
        polymorphismKey >= 0
          ? window?.injector?.Deserialize?.getPolymorphicObject(polymorphismKey, data)
          : Object.assign(Object.create(ObjectType.prototype), data);
      if (clearMethodCache) {
        // eslint-disable-next-line no-underscore-dangle
        (instance as any)._memoize = undefined;
        // eslint-disable-next-line no-underscore-dangle
        (instance as any)._cache = undefined;
      }
      instance?.onDeserialize();
      return instance as T;
    }
  }

  static arrayOf<T extends Deserializable>(ObjectType: Type<T>, data: any[]): T[] {
    if (!data) {
      return null;
    } else {
      const arr: T[] = [];
      data.forEach(d => {
        const inst: T = window?.injector?.Deserialize?.instanceOf(ObjectType, d);
        if (exists(inst)) arr.push(inst);
      });
      return arr;
    }
  }

  static mapOf<X extends string | number, Y extends Deserializable>(ObjectType: Type<Y>, data: Map<X, Y>): Map<X, Y> {
    if (!data) {
      return new Map();
    } else {
      const newMap = new Map<X, Y>();
      const dataMap: Map<any, any> = data instanceof Map ? data : new Map(Object.entries(data));
      if (dataMap instanceof Map) {
        dataMap.forEach((val, key) => newMap.set(key as X, this.instanceOf(ObjectType, val)));
      }
      return newMap;
    }
  }

  private static getPolymorphicObject<T extends Deserializable>(key: PolymorphicDeserializationKey, data: any): T {
    const getPolymorphicObjectFromKey = {
      [PolymorphicDeserializationKey.Menu]: DeserializeMenuHelper.getPolymorphicMenuObject,
      [PolymorphicDeserializationKey.Section]: DeserializeSectionHelper.getPolymorphicSectionObject,
      [PolymorphicDeserializationKey.Label]: DeserializeLabelHelper.getPolymorphicLabelObject,
      [PolymorphicDeserializationKey.Display]: DeserializeDisplayHelper.getPolymorphicDisplayObject,
      [PolymorphicDeserializationKey.WebSocketMessage]: DeserializeWebSocketMessageHelper.getPolymorphicMessageObject
    };
    return getPolymorphicObjectFromKey?.[key]?.(data) || null;
  }

  static genericMap<X extends string | number, Y>(data: Map<X, Y>): Map<X, Y> {
    if (!data) {
      return null;
    } else {
      const dataMap: Map<any, any> = data instanceof Map ? data : new Map(Object.entries(data));
      return dataMap.deepCopy();
    }
  }

  static genericArrayMap<X extends string | number, Y extends string | number>(data: Map<X, Y[]>): Map<X, Y[]> {
    if (!data) {
      return null;
    } else {
      const newMap = new Map<X, Y[]>();
      const dataMap = data instanceof Map ? data : new Map(Object.entries(data));
      if (dataMap instanceof Map) {
        dataMap.forEach((val, key) => newMap.set(key as X, val as Y[]));
      }
      return newMap;
    }
  }

  static typedArrayMapOf<X extends string | number, Y extends Deserializable>(
    ObjectType: Type<Y>,
    data: Map<X, Y[]>
  ): Map<X, Y[]> {
    if (!data) {
      return null;
    } else {
      const newMap = new Map<X, Y[]>();
      const dataMap = data instanceof Map ? data : new Map(Object.entries(data));
      if (dataMap instanceof Map) {
        dataMap.forEach((val, key) => newMap.set(key as X, this.arrayOf(ObjectType, val)));
      }
      return newMap;
    }
  }
}
